import { LAUNCH_ARTICLES } from "./launchArticles";

export type ResourceGuide = {
  path: string;
  publishedAt?: string;
  updatedAt?: string;
  eyebrow: string;
  title: string;
  description: string;
  introduction: readonly string[];
  sections: readonly {
    id: string;
    title: string;
    paragraphs: readonly string[];
    bullets?: readonly string[];
    table?: {
      caption: string;
      columns: readonly string[];
      rows: readonly (readonly string[])[];
    };
  }[];
  sources: readonly { label: string; url: string }[];
  related: readonly (readonly [string, string, string])[];
};

export const RESOURCE_GUIDES: readonly ResourceGuide[] = [
  ...LAUNCH_ARTICLES,
  {
    path: "/resources/dkim-guide",
    eyebrow: "Domain authentication guide",
    title: "DKIM for business email: selectors, signing and verification.",
    description: "Understand what DKIM proves, how selectors locate public keys, how to verify a record and what to check when signed email fails authentication.",
    introduction: [
      "DomainKeys Identified Mail, or DKIM, lets a sending system attach a cryptographic signature to a message. A receiving system retrieves the public key published in DNS and uses it to check whether selected signed headers and the message body still match the signature.",
      "A DKIM pass does not prove that every statement in a message is trustworthy, and it does not replace SPF or DMARC. It provides evidence that the signed content survived transit and that the signer controlled the private key associated with the published selector record.",
    ],
    sections: [
      {
        id: "selector-and-record",
        title: "How the selector and DNS record fit together",
        paragraphs: [
          "The DKIM-Signature header identifies a signing domain in the d= tag and a selector in the s= tag. The receiver combines them to query a TXT record at selector._domainkey.example.com. That record normally identifies the key type and contains the public key used for verification.",
          "Selectors allow a domain to rotate keys or operate more than one approved sending platform without publishing one shared key. Each platform should provide the exact selector name and record value its signer expects.",
        ],
        bullets: [
          "Publish the selector at the precise hostname supplied by the sender.",
          "Keep the private key in the sending system; only the public key belongs in DNS.",
          "Use separate selectors when platforms or key-rotation schedules differ.",
        ],
      },
      {
        id: "verification",
        title: "A practical verification sequence",
        paragraphs: [
          "First confirm which service sends mail for the domain and obtain its current selector. Query the selector record, confirm that one usable DKIM key is returned, and send a controlled message to a mailbox where the full authentication results can be inspected.",
          "Check that the message contains a DKIM-Signature header, that the receiving system reports dkim=pass, and that the d= domain aligns with the visible From domain when DMARC relies on DKIM alignment.",
        ],
        bullets: [
          "Verify the selector rather than guessing common names such as default or google.",
          "Allow for DNS caching after a record is added or replaced.",
          "Test each service that sends as the domain, including marketing and transaction platforms.",
        ],
      },
      {
        id: "failures",
        title: "Why DKIM checks fail",
        paragraphs: [
          "A missing selector, a record published at the wrong DNS name, a truncated public key, or a signer using an old private key can prevent verification. Message modification after signing can also break the body or header hash.",
          "When a record exists but mail still fails, compare the selector and signing domain in the actual message header with the DNS name being checked. That evidence is more reliable than assuming the administrative interface and production signer are using the same configuration.",
        ],
      },
      {
        id: "envelaro-check",
        title: "Check the public evidence",
        paragraphs: [
          "The Envelaro DKIM checker retrieves the public selector record you specify and explains common record-level problems. It cannot see a provider's private key or prove that every outgoing message is being signed, so complete verification still requires a real message-header test.",
        ],
      },
    ],
    sources: [
      { label: "RFC 6376 — DomainKeys Identified Mail (DKIM) Signatures", url: "https://www.rfc-editor.org/rfc/rfc6376" },
      { label: "Google Workspace Admin Help — Set up DKIM", url: "https://support.google.com/a/answer/174124" },
      { label: "Microsoft Learn — Configure DKIM for custom domains", url: "https://learn.microsoft.com/en-us/defender-office-365/email-authentication-dkim-configure" },
    ],
    related: [
      ["Run the DKIM checker", "/tools/dkim-checker", "Query a known selector and inspect the published public key."],
      ["Read the DMARC guide", "/resources/dmarc-guide", "Connect DKIM and SPF results to domain alignment and policy."],
      ["Check overall email health", "/tools/email-health", "Review the domain's public MX, SPF, DKIM and DMARC evidence."],
    ],
  },
  {
    path: "/resources/dmarc-guide",
    eyebrow: "Domain policy guide",
    title: "DMARC for business email: alignment, reporting and rollout.",
    description: "Learn how DMARC evaluates aligned SPF or DKIM results, how policy records are published, and how to move from monitoring toward enforcement safely.",
    introduction: [
      "Domain-based Message Authentication, Reporting and Conformance, or DMARC, connects the domain visible in the From header with SPF and DKIM authentication. A message can pass DMARC when at least one of those mechanisms both passes and aligns with the visible From domain.",
      "DMARC also lets a domain owner publish handling guidance for messages that fail and request aggregate reports. A policy should be introduced with evidence: inventory legitimate senders, review alignment, monitor reports and only then increase enforcement.",
    ],
    sections: [
      {
        id: "alignment",
        title: "Authentication is not the same as alignment",
        paragraphs: [
          "SPF checks the domain used for the SMTP envelope sender, while DKIM checks the signing domain in the DKIM signature. DMARC compares those authenticated domains with the domain shown to the recipient in the From header.",
          "This distinction matters when a third-party platform sends for a business. The platform may authenticate its own infrastructure successfully while still failing DMARC for the customer's visible domain unless aligned return-path or DKIM signing is configured.",
        ],
      },
      {
        id: "record",
        title: "What belongs in a DMARC record",
        paragraphs: [
          "The policy is published as a TXT record at _dmarc.example.com. The record begins with v=DMARC1 and includes a p= policy. Common optional tags control aggregate reporting, subdomain policy and alignment strictness.",
        ],
        bullets: [
          "Use p=none to collect evidence without requesting rejection or quarantine.",
          "Use rua= with a mailbox or reporting service prepared to process aggregate XML reports.",
          "Treat pct, subdomain policy and strict alignment as deliberate operational controls, not decorative tags.",
        ],
      },
      {
        id: "rollout",
        title: "Roll out enforcement in controlled stages",
        paragraphs: [
          "Start by listing every legitimate source that uses the domain in visible From addresses. Confirm aligned SPF or DKIM for each source and monitor DMARC aggregate reports long enough to identify forgotten applications and regional systems.",
          "After legitimate traffic consistently aligns, move toward quarantine or reject according to the organization's risk tolerance. Continue reviewing reports after enforcement because new senders, DNS changes and key rotation can introduce regressions.",
        ],
      },
      {
        id: "limits",
        title: "What a public DMARC check can and cannot prove",
        paragraphs: [
          "A DNS check can confirm that a syntactically usable record is published and explain its policy tags. It cannot prove that every legitimate sender aligns, that report mailboxes are processing data correctly, or that enforcement will be safe for a particular organization.",
        ],
      },
    ],
    sources: [
      { label: "RFC 7489 — Domain-based Message Authentication, Reporting, and Conformance", url: "https://www.rfc-editor.org/rfc/rfc7489" },
      { label: "Google Workspace Admin Help — Set up DMARC", url: "https://support.google.com/a/answer/2466580" },
      { label: "Microsoft Learn — Set up DMARC for outbound mail", url: "https://learn.microsoft.com/en-us/defender-office-365/email-authentication-dmarc-configure" },
    ],
    related: [
      ["Run the DMARC checker", "/tools/dmarc-checker", "Inspect the published policy, reporting and alignment tags."],
      ["Read the DKIM guide", "/resources/dkim-guide", "Understand selectors, signing domains and public-key verification."],
      ["Plan managed setup", "/managed-email-setup", "Coordinate sender inventory, DNS publication and mail-flow testing."],
    ],
  },
  {
    path: "/resources/email-deliverability-guide",
    eyebrow: "Sender operations guide",
    title: "Business email deliverability: controls that work together.",
    description: "A practical framework for authentication, recipient permission, sender reputation, message handling and ongoing deliverability monitoring.",
    introduction: [
      "Email deliverability is the result of several systems working together. Correct DNS is necessary, but it does not guarantee inbox placement. Receiving providers also evaluate sending behavior, recipient engagement, complaint patterns, message content, connection quality and the reputation of domains and IP addresses.",
      "A dependable program separates configuration evidence from operational evidence. DNS checks show what is published; message headers show how a particular message authenticated; delivery logs, bounce responses and complaint data show how receiving systems handled traffic over time.",
    ],
    sections: [
      {
        id: "identity",
        title: "Establish a consistent sending identity",
        paragraphs: [
          "Publish one valid SPF policy for each sending domain, sign outbound mail with DKIM, and use DMARC to evaluate alignment with the visible From domain. Ensure forward and reverse DNS, TLS and message formatting meet the requirements of the infrastructure being used.",
        ],
        bullets: [
          "Inventory every service authorized to send as the domain.",
          "Keep SPF within its DNS-lookup limits and remove obsolete senders.",
          "Rotate DKIM keys according to provider capability and incident risk.",
          "Move DMARC toward enforcement only after legitimate traffic aligns.",
        ],
      },
      {
        id: "recipients",
        title: "Send mail people expect to receive",
        paragraphs: [
          "For marketing and bulk traffic, use permission-based lists, clear sender identity and a working unsubscribe process. Do not purchase address lists or continue mailing recipients who repeatedly bounce or complain.",
          "Transactional and person-to-person messages should also use accurate identities and monitored reply paths. Separate traffic streams when their purpose, volume or risk profile differs so a problem in one stream does not obscure another.",
        ],
      },
      {
        id: "operations",
        title: "Monitor outcomes instead of guessing",
        paragraphs: [
          "Review SMTP response codes, deferred delivery, hard and soft bounces, complaint feedback where available, and provider postmaster dashboards. A blocklist result is one signal, not a complete deliverability diagnosis.",
          "When performance changes, correlate the timing with DNS changes, new sending sources, volume spikes, compromised accounts and content changes. Preserve evidence before changing several controls at once.",
        ],
      },
      {
        id: "incident-response",
        title: "Respond to reputation incidents methodically",
        paragraphs: [
          "Stop abusive or compromised sending, reset affected credentials, identify the source, correct recipient-list or application behavior, and document remediation. Follow each receiving provider or blocklist operator's published process without promising an immediate removal or inbox-placement result.",
        ],
      },
    ],
    sources: [
      { label: "Google — Email sender guidelines", url: "https://support.google.com/a/answer/81126" },
      { label: "Yahoo Sender Hub — Sender best practices", url: "https://senders.yahooinc.com/best-practices/" },
      { label: "RFC 7208 — Sender Policy Framework", url: "https://www.rfc-editor.org/rfc/rfc7208" },
      { label: "RFC 6376 — DKIM Signatures", url: "https://www.rfc-editor.org/rfc/rfc6376" },
      { label: "RFC 7489 — DMARC", url: "https://www.rfc-editor.org/rfc/rfc7489" },
    ],
    related: [
      ["Check domain authentication", "/tools/email-health", "Review public MX, SPF, DKIM and DMARC evidence."],
      ["Check an IP address", "/tools/ip-blocklist-checker", "Query named public DNS blocklists for a sending IPv4 address."],
      ["Review Envelaro security", "/security", "Understand the service's authentication and administrative approach."],
    ],
  },
  {
    path: "/resources/business-email-provider-comparison",
    updatedAt: "2026-09-20",
    eyebrow: "Provider decision guide",
    title: "Business email provider comparison checklist.",
    description: "Build a side-by-side shortlist with the same questions about cost, domains, storage, migration, security and support for every provider.",
    introduction: [
      "A useful business-email comparison starts with the organization's operating model. A familiar mailbox interface matters, but so do domain ownership, administrator access, shared resources, migration methods, authentication, retention, storage allocation, support and the dependencies bundled with the current provider.",
      "Provider features and prices change. Use this framework to create a current shortlist, then verify each requirement against the provider's official documentation and the commercial offer presented to your organization.",
      "Start with your current mailbox count, owned domains, mail storage and applications that rely on email. Copy the checklist below into your decision document, add a column for each provider and record the answer, source and date. Mark unanswered requirements as unconfirmed rather than treating them as included.",
    ],
    sections: [
      {
        id: "comparison-checklist",
        title: "Ask every provider the same questions",
        paragraphs: ["Compare the plan you would actually buy. Record written answers for your organization, including any extra charges or work your administrator must do."],
        table: {
          caption: "Business email provider evaluation checklist",
          columns: ["Decision area", "Record for each provider", "Evidence to request"],
          rows: [
            ["Total cost", "First-year and renewal totals for your actual mailbox count; billing term; taxes; storage, migration and support charges.", "A dated quote and the applicable billing and cancellation terms."],
            ["Domains and ownership", "Allowed domains, ownership checks, aliases and who controls domain registration, DNS and account recovery.", "Plan limits and a written list of administrator responsibilities."],
            ["Storage and limits", "Usable mail storage, whether it is shared or per mailbox, attachment and sending limits, and the cost of growth.", "The selected plan's capacity and usage documentation."],
            ["Migration scope", "Treatment of messages, folders, aliases, shared mailboxes, calendars and contacts; access method; cutover and rollback responsibilities.", "A source-specific migration plan with exclusions and acceptance checks."],
            ["Applications and identity", "Desktop and mobile client requirements, website or application senders, and documents or sign-in services that must remain elsewhere.", "Supported connection methods and a test using your actual applications."],
            ["Security and recovery", "Available MFA and administrator controls, domain authentication responsibilities, retention, backups and restore boundaries.", "Current security documentation and written recovery and retention terms."],
            ["Support and exit", "Support hours and channels, escalation route, export formats, cancellation steps and the data-access period after service ends.", "Applicable support commitments, export instructions and service terms."],
          ],
        },
      },
      {
        id: "service-model",
        title: "Compare the service model first",
        paragraphs: [
          "Per-user productivity suites can combine email with broad document and collaboration ecosystems. Hosting-control-panel mail may be bundled with a website account. Managed business-email services can focus more directly on domain setup, migration and administration. None of these categories is automatically best for every organization.",
        ],
        bullets: [
          "List the workloads that must move together and those that can remain separate.",
          "Confirm whether pricing is per user, per organization, by storage, or a combination.",
          "Identify which administrator owns domains, identities, billing and recovery.",
        ],
      },
      {
        id: "migration",
        title: "Treat migration capability as a requirement",
        paragraphs: [
          "Ask how historical mail, folders, aliases, groups, shared mailboxes, calendars, contacts and archives are transferred. An IMAP copy may move messages while leaving other workloads and permissions behind.",
          "The source platform's administrator and export controls affect the available method. Google Workspace, Microsoft Exchange, Zoho Mail and cPanel each document different migration or export paths, so the source must be identified precisely before effort or downtime is estimated.",
        ],
      },
      {
        id: "dns-and-security",
        title: "Verify DNS and security responsibilities",
        paragraphs: [
          "Confirm who supplies and validates MX, SPF, DKIM and DMARC records, who approves DNS publication, and how unrelated website or application records are protected during cutover. Ask about MFA, administrator roles, audit evidence, backup boundaries and account recovery.",
        ],
      },
      {
        id: "decision-record",
        title: "Build a decision record your administrator can defend",
        paragraphs: [
          "Record current mailbox counts, domains, storage, shared resources, retention needs, client requirements, migration deadlines and support expectations. Compare written answers and official documentation rather than assuming similar feature names represent equivalent operational commitments.",
          "Envelaro publishes organization-level plans and a managed migration sequence. Organizations should compare those documented boundaries with the current offers and requirements of every shortlisted provider before purchasing.",
        ],
        bullets: [
          "Choose your non-negotiable requirements before reviewing prices, such as a supported mail client or an agreed export path.",
          "Separate confirmed capabilities, paid extras, work owned by your administrator and requirements still awaiting an answer.",
          "Test a representative mailbox and the applications that send mail before committing to a full migration.",
          "Keep a dated decision record with the chosen plan, accepted limitations, migration owner and completion checks.",
        ],
      },
    ],
    sources: [
      { label: "Google Workspace Admin Help — Migrate an organization's data", url: "https://support.google.com/a/topic/6245191" },
      { label: "Microsoft Learn — Mailbox migration", url: "https://learn.microsoft.com/en-us/exchange/mailbox-migration/mailbox-migration" },
      { label: "Zoho Mail — IMAP migration", url: "https://www.zoho.com/mail/help/adminconsole/imap-migration.html" },
      { label: "cPanel documentation — Email Accounts", url: "https://docs.cpanel.net/cpanel/email/email-accounts/" },
    ],
    related: [
      ["Compare Envelaro plans", "/pricing", "Review current organization-level capacity and pricing."],
      ["Review the migration sequence", "/migration", "See the discovery, preparation, copy, cutover and validation stages."],
      ["Discuss your current provider", "/contact", "Share the source platform, mailbox count, dependencies and deadline."],
    ],
  },
] as const;

export const RESOURCE_GUIDE_BY_PATH = new Map(
  RESOURCE_GUIDES.map((guide) => [guide.path, guide]),
);
